import { Component, OnInit, signal, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { ConfirmationService, MenuItem, MessageService, SortEvent } from 'primeng/api';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef
} from 'primeng/dynamicdialog';
import { Paginator } from 'primeng/paginator';
import { ORGANIZATION_PERMISSION_TYPE } from 'src/app/common/model/permission.model';
import { FileImportComponent } from 'src/app/common/modules/document/file-import/file-import.component';
import { DeleteConfirmDialogComponent } from 'src/app/common/modules/shared/components/delete-confirm-dialog/deleteConfirmDialog.component';
import { UtilService } from 'src/app/common/services/util/util.service';
import { ICategory, IItem } from 'src/app/EBMSuite/models/item.model';
import { ItemCategoryService } from 'src/app/EBMSuite/services/item-categories/item-category.service';
import { ItemsService } from 'src/app/EBMSuite/services/items/items.service';
import { ReportService } from 'src/app/EBMSuite/services/report/report.service';
import { AddItemComponent } from '../add-item/add-item.component';

@Component({
  selector: 'app-all-items',
  templateUrl: './all-items.component.html',
  styleUrls: ['./all-items.component.scss'],
  providers: [DialogService, MessageService, ConfirmationService]
})
export class AllItemsComponent implements OnInit {
  @ViewChild('paginator', { static: true }) paginator!: Paginator;

  items = signal<IItem[]>([]);
  topItems: any[] = [];
  stockMetrics: {
    itemsInStock: number;
    itemsOnLowStock: number;
    outOfStockItems: number;
  } = {
    itemsInStock: 0,
    itemsOnLowStock: 0,
    outOfStockItems: 0
  };
  subscription = new Subscription();
  ORGANIZATION_PERMISSION_TYPE = ORGANIZATION_PERMISSION_TYPE;

  tableColumns: {
    displayName: string;
    fieldName: string;
  }[] = [
    {
      displayName: 'name',
      fieldName: 'name'
    },
    {
      displayName: 'type',
      fieldName: 'itemType'
    },
    {
      displayName: 'stock quantity',
      fieldName: 'quantity'
    },
    {
      displayName: 'stock status',
      fieldName: 'instock'
    },
    {
      displayName: 'cost',
      fieldName: 'cost'
    },
    {
      displayName: 'price',
      fieldName: 'price'
    }
  ];

  rows = 50;
  allRecordsCount = 0;
  modalDismissed = false;

  defaultPagination = {
    page: 0,
    pageSize: this.rows,
    searchParam: '',
    sortKey: 'createdAt',
    sortDirection: 1,
    isWebRequest: true
  };
  searchText = '';
  initialSearch = '';
  allItemsCount = 0;
  exportingItems = false;
  form = new FormGroup({});

  itemCategory!: ICategory;
  isChecked = false;
  checkedItems: IItem[] = [];

  ref!: DynamicDialogRef;

  itemUploadQuantityOptionDialog = false;
  itemsToUpload!: IItem[];
  itemToUploadColTitles!: string[];
  itemsToUploadWithTrackInventory!: IItem[];

  excelUploadFields: {
    systemField: string;
    userProvidedField: string | null;
  }[] = [
    {
      systemField: 'name',
      userProvidedField: null
    },
    {
      systemField: 'price',
      userProvidedField: null
    },
    {
      systemField: 'cost',
      userProvidedField: null
    },
    {
      systemField: 'quantity',
      userProvidedField: null
    },
    {
      systemField: 'itemType',
      userProvidedField: null
    },
    {
      systemField: 'trackInventory',
      userProvidedField: null
    },
    {
      systemField: 'reorderLevel',
      userProvidedField: null
    },
    {
      systemField: 'unitOfMeasure',
      userProvidedField: null
    }
  ];

  selectAllItemsToUpload = true;

  filterItems: MenuItem[] = [
    {
      label: 'Item type',
      items: [
        {
          label: 'Inventory',
          value: 'INVENTORYITEM',
          command: () => {
            this.searchText = 'INVENTORYITEM';
            this.getSearchResults();
          }
        },
        {
          label: 'Services',
          value: 'SERVICEITEM',
          command: () => {
            this.searchText = 'SERVICEITEM';
            this.getSearchResults();
          }
        },
        {
          label: 'Bookable',
          value: 'BOOKABLERESOURCE',
          command: () => {
            this.searchText = 'BOOKABLERESOURCE';
            this.getSearchResults();
          }
        }
      ]
    }
  ];

  constructor(
    private itemsService: ItemsService,
    public utilService: UtilService,
    public itemCategoryService: ItemCategoryService,
    public router: Router,
    public dialogService: DialogService,
    public config: DynamicDialogConfig,
    private reportService: ReportService,
    public dynamicRef: DynamicDialogRef
  ) {}

  ngOnInit(): void {
    this.getAllItemsCount();
    this.getPaginatedItems();
    this.getStockMetrics();
    if (this.utilService.doesURLHaveText('all-categories')) {
      this.itemCategory = this.config.data.itemCategory;
      this.checkedItems = [...this.itemCategory.items];
    }
    this.isSelectDialog();
  }

  getAllItems(): void {
    this.exportingItems = true;
    this.subscription.add(
      this.itemsService.getItems().subscribe({
        next: (res) => {
          const allItems: IItem[] = res.result.map((x: any) => {
            const items = x;
            [
              '_id',
              'updatedBy',
              'createdBy',
              'createdAt',
              'updatedAt',
              '__v',
              'organization',
              'isTaxed',
              'lastModifiedBy',
              'initialStock ',
              'taxes'
            ].forEach((e) => {
              delete items[e as keyof IItem];
            });
            return items;
          });
          this.exportItems(allItems);
          this.exportingItems = false;
        }
      })
    );
  }

  public openDialog(item: any) {
    const ref = this.dialogService.open(DeleteConfirmDialogComponent, {
      header: 'Delete Confirmation',
      width: '50%',
      baseZIndex: 10000
    });
    ref.onClose.subscribe((confirm) => {
      if (confirm) {
        this.deleteItem(item._id);
      }
    });
  }

  deleteItem(itemId: string): void {
    this.subscription.add(
      this.itemsService.deleteItem(itemId).subscribe({
        complete: () => {
          this.getPaginatedItems(), this.getAllItemsCount();
          this.getStockMetrics();
        }
      })
    );
  }

  deleteItemMany(items: IItem[]): void {
    const selectedItems = items.map((item) => item._id);
    this.subscription.add(
      this.itemsService.bulkDeleteItems({ items: selectedItems }).subscribe({
        complete: () => {
          this.getPaginatedItems(), this.getAllItemsCount();
          this.getStockMetrics();
        }
      })
    );
  }

  getSortedPaginatedItems(e: SortEvent) {
    let { field, order } = e;
    if (field!.includes('.')) {
      field = field!.split('.')[0];
    }

    if (
      field === this.defaultPagination.sortKey &&
      order === this.defaultPagination.sortDirection
    )
      return;

    this.defaultPagination = {
      ...this.defaultPagination,
      sortKey: field ?? 'createdAt',
      sortDirection: order ?? 1
    };
    this.getPaginatedItems();
  }

  getPaginatedItems(): void {
    this.subscription.add(
      this.itemsService.getPaginatedItems(this.defaultPagination).subscribe({
        next: (res) => {
          this.items.set(
            res.result?.data?.length > 0
              ? res.result?.data.map((x: IItem) => this.appendDropdownItemsToItem(x))
              : []
          );

          this.getTopSoldItems();
        }
      })
    );
  }

  paginate(event: any): void {
    this.defaultPagination = {
      ...this.defaultPagination,
      page: event.page,
      pageSize: this.rows,
      searchParam: this.searchText
    };

    this.getPaginatedItems();
  }

  getAllItemsCount(): void {
    this.subscription.add(
      this.itemsService.getAllItemsCount().subscribe({
        next: (res) => {
          this.allItemsCount = res.result;
        }
      })
    );
  }

  getAllSearchedItemsCount(): void {
    this.subscription.add(
      this.itemsService.getAllSearchedItemsCount(this.searchText).subscribe({
        next: (res) => {
          this.allItemsCount = res.result;
        }
      })
    );
  }
  searchOnEnter(searchInput: string) {
    this.searchText = searchInput;
    this.getSearchResults();
  }

  onSearchTermChange(searchInput: string): void {
    if (searchInput === '') {
      this.searchText = searchInput;
      this.getSearchResults();
    }
  }

  getSearchResults(): void {
    setTimeout(() => this.paginator.changePage(0));
    if (this.searchText) {
      this.getAllSearchedItemsCount();
      if (this.initialSearch?.toLowerCase() !== this.searchText?.toLowerCase()) {
        this.initialSearch = this.searchText;
        this.defaultPagination = {
          ...this.defaultPagination,
          page: 0,
          searchParam: this.searchText
        };
      } else {
        this.defaultPagination = {
          ...this.defaultPagination,
          searchParam: this.searchText
        };
      }
    } else {
      this.getAllItemsCount();
      this.defaultPagination = {
        ...this.defaultPagination,
        page: 0,
        searchParam: ''
      };
    }
    this.getPaginatedItems();
  }

  clearSearch() {
    this.searchText = '';
    this.getSearchResults();
  }

  exportItems(items: IItem[]): void {
    this.utilService.exportAsExcelFile(items, 'All-Items');
  }

  getStockMetrics(): void {
    this.subscription.add(
      this.itemsService.getStockMetrics().subscribe({
        next: (res) => {
          this.stockMetrics = res.result;
        }
      })
    );
  }

  getTopSoldItems(): void {
    const range = {
      type: 'YEAR',
      start: new Date().getFullYear(),
      end: new Date().getFullYear()
    };
    this.subscription.add(
      this.reportService.getTopSoldItems(range).subscribe({
        next: (res) => {
          this.topItems = res.result === 0 ? [] : res.result.itemsSoldByQuantity;
          if (this.topItems) {
            this.items.set(
              this.items().map((item) => {
                const isTopItem = this.topItems.some((x) => x.item === item._id);
                if (isTopItem) {
                  item.top = true;
                }
                return item;
              })
            );
          }
          // Reorder the items array so that top items appear at the top
          this.items.set(
            this.items().sort((a, b) => {
              if (a.top && !b.top) {
                return -1; // a should come before b
              } else if (!a.top && b.top) {
                return 1; // b should come before a
              } else {
                return 0; // no change in order
              }
            })
          );
        }
      })
    );
  }

  addToCategory() {
    if (this.dynamicRef) {
      this.dynamicRef.close(this.checkedItems);
    } else if (this.itemCategory && this.itemCategory._id) {
      this.itemCategory = {
        ...this.itemCategory,
        items: [...this.itemCategory.items, ...this.checkedItems]
      };

      this.itemCategoryService
        .editCategory(this.itemCategory._id, this.itemCategory)
        .subscribe((res) => {});
    }
  }

  appendDropdownItemsToItem(item: IItem): IItem {
    const tableActionButtons = [
      {
        label: 'Restock',
        icon: 'pi pi-plus',
        routerLink: ['../details/' + item?._id + '/restock']
      },
      {
        label: 'Edit',
        icon: 'pi pi-pencil',
        routerLink: ['../edit-item/' + item?._id]
      },
      {
        label: 'Delete',
        icon: 'pi pi-times',
        command: () => this.openDialog(item)
      }
    ];
    item.menuItems = item.trackInventory
      ? tableActionButtons
      : tableActionButtons.filter((x) => x.label.toLowerCase() !== 'restock');
    return item;
  }

  openFileUploadModal() {
    this.ref = this.dialogService.open(FileImportComponent, {
      header: 'File Upload',
      height: '35%',
      width: '70%',
      data: 'items'
    });
    this.ref.onClose.subscribe((res) => {
      if (res) {
        this.itemsToUpload = res;
        // this.itemsToUpload = res.worksheet;
        // this.itemToUploadColTitles = res.titles;
        this.itemsToUploadWithTrackInventory = this.itemsToUpload.filter((item: any) => {
          const trackInventoryBoolean = item.trackInventory === 'TRUE' ? true : false;
          return trackInventoryBoolean;
        });
        this.selectItemUploadQuantityOption();
      }
    });
  }

  openAddItemDialog() {
    this.ref = this.dialogService.open(AddItemComponent, {
      header: 'Add Item',
      height: '100%',
      width: '70%',
      data: 'items'
    });
    this.ref.onClose.subscribe((res: IItem) => {
      if (res) {
        console.log('res', res);
        this.checkedItems.push(res);
        this.dynamicRef.close(this.checkedItems);
      }
    });
  }
  selectItemUploadQuantityOption() {
    this.itemUploadQuantityOptionDialog = true;
    this.selectAllItemsToUploadFunc();
  }

  addBulkItems(): void {
    const filteredItemsToUpload = this.itemsToUpload.map((item) => {
      const existsInInventory = this.itemsToUploadWithTrackInventory.some(
        (inventoryItem) => inventoryItem._id === item._id
      );
      return { ...item, existsInInventory };
    });
    this.subscription.add(
      this.itemsService
        .addBulkItemsWithQuantityUpdate(filteredItemsToUpload as any)
        .subscribe({
          complete: () => {
            this.ref.close();
            this.itemUploadQuantityOptionDialog = false;
            this.getPaginatedItems();
            this.getAllItemsCount();
          }
        })
    );
  }

  selectAllItemsToUploadFunc(): void {
    this.itemsToUploadWithTrackInventory.forEach((item) => {
      item.updateQuantity = this.selectAllItemsToUpload;
    });
  }

  isSelectDialog(): boolean {
    return (
      this.utilService.doesURLHaveText('all-categories') ||
      this.utilService.doesURLHaveText('invoice') ||
      this.utilService.doesURLHaveText('quotes') ||
      this.utilService.doesURLHaveText('CUSTOMER')
    );
  }
}
